import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import Lenis from "lenis";
import "./Home.scss";
import NoiseShaderTVGrainAnimation from "../shaders/NoiseShaderTVGrainAnimation";
import { renderRichContent } from "../utils/renderRichContent";
import imageLoaderSanity from "../utils/imageLoaderSanity";

const Home = ({ aboutProject, projects, shaderOn }) => {
  const [hoveredProjectIdx, setHoveredProjectIdx] = useState(null);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);

  // Get all projects
  const baseProjects = useMemo(() => projects || [], [projects]);
  const [displayProjects, setDisplayProjects] = useState([]);
  const sentinelRef = useRef(null);
  const listRef = useRef(null);
  const listContentRef = useRef(null);

  useEffect(() => {
    setDisplayProjects(baseProjects);
    if (baseProjects.length === 0) return;

    setAllImagesLoaded(false);
    const srcs = baseProjects
      .map((p) => imageLoaderSanity(p.picture || p.lowResPicture))
      .filter(Boolean);

    if (srcs.length === 0) {
      setAllImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    const total = srcs.length;
    const imgs = srcs.map((src) => {
      const img = new window.Image();
      img.onload = img.onerror = () => {
        loadedCount += 1;
        if (loadedCount >= total) setAllImagesLoaded(true);
      };
      img.src = src;
      return img;
    });

    return () => {
      imgs.forEach((img) => {
        img.onload = null;
        img.onerror = null;
      });
    };
  }, [baseProjects]);

  const appendMore = useCallback(() => {
    setDisplayProjects((prev) => [...prev, ...baseProjects]);
  }, [baseProjects]);

  useEffect(() => {
    if (!sentinelRef.current || baseProjects.length === 0 || !listRef.current)
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) appendMore();
      },
      { threshold: 0.1, root: listRef.current },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [appendMore, baseProjects.length]);

  useEffect(() => {
    if (!listRef.current || !listContentRef.current) return;
    const lenis = new Lenis({
      wrapper: listRef.current,
      content: listContentRef.current,
      smoothWheel: true,
      wheelMultiplier: 0.8,
      touchMultiplier: 0.8,
    });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div
      className={`homepage scrollable-homepage ${allImagesLoaded ? "images-loaded" : ""}`}
    >
      <NoiseShaderTVGrainAnimation visible={shaderOn} />
      {!allImagesLoaded && baseProjects.length > 0 && (
        <div className="loading-overlay">loading</div>
      )}
      <div className="about-content">
        <div className="about-content-bg" aria-hidden="true" />
        <div className="project-description">
          {aboutProject &&
            renderRichContent(
              aboutProject.descriptionRich || aboutProject.description,
            )}
        </div>
      </div>
      <div className="project-list-vertical" ref={listRef}>
        <div ref={listContentRef}>
          {displayProjects.map((project, idx) => (
            <div key={idx} className="project-list-item">
              <div className="project-list-img-info-row">
                <div className="project-list-img-wrapper">
                  <div className="project-list-title-sticky">
                    {project.title}
                  </div>
                  {project.url ? (
                    <a
                      className="project-list-img-link"
                      href={project.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setHoveredProjectIdx(idx)}
                      onMouseLeave={() => setHoveredProjectIdx(null)}
                    >
                      <img
                        src={imageLoaderSanity(
                          project.picture || project.lowResPicture,
                        )}
                        alt={project.title}
                        className="project-list-img"
                      />
                    </a>
                  ) : (
                    <div
                      className="project-list-img-link"
                      onMouseEnter={() => setHoveredProjectIdx(idx)}
                      onMouseLeave={() => setHoveredProjectIdx(null)}
                    >
                      <img
                        src={imageLoaderSanity(
                          project.picture || project.lowResPicture,
                        )}
                        alt={project.title}
                        className="project-list-img"
                      />
                    </div>
                  )}
                  {hoveredProjectIdx === idx &&
                    (project.descriptionRich || project.description) && (
                      <div className="project-list-info-overlay">
                        {renderRichContent(
                          project.descriptionRich || project.description,
                        )}
                        <span className="project-hover-year">
                          {project.year}
                        </span>
                      </div>
                    )}
                  <div className="project-list-year-bottom-right">
                    {project.year}
                  </div>
                  {(project.descriptionRich || project.description) && (
                    <div className="project-list-mobile-desc">
                      {renderRichContent(
                        project.descriptionRich || project.description,
                      )}
                      {project.year && (
                        <span className="project-hover-year">
                          {" "}
                          {project.year}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="project-list-info-outside">
                  {/* Description removed as requested */}
                </div>
              </div>
            </div>
          ))}
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
      </div>
    </div>
  );
};

export default Home;
